import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarOff, ArrowLeft, ChevronLeft, ChevronRight, CalendarClock, CalendarDays, Grid3x3, Copy, Check, ArrowRightCircle, ShieldCheck, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { useAuth } from "../../context/AuthContext";
import { API } from "../../lib/api";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const authed = { withCredentials: true };
const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const endOfMonth = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0);
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const weekStart = (d) => addDays(d, -((d.getDay() + 6) % 7)); // Monday
const fmtTime = (s) => s ? new Date(s).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/London" }) : "";
const relTime = (iso) => {
  if (!iso) return null;
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs} hour${hrs > 1 ? "s" : ""} ago`;
  const days = Math.round(hrs / 24);
  return `${days} day${days > 1 ? "s" : ""} ago`;
};

export default function Availability() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [advisers, setAdvisers] = useState([]);
  const [me, setMe] = useState(null);
  const [selected, setSelected] = useState("");
  const [items, setItems] = useState([]);
  const [appts, setAppts] = useState([]);
  const [view, setView] = useState("month");
  const [cursor, setCursor] = useState(startOfMonth(new Date()));
  const [weekAnchor, setWeekAnchor] = useState(weekStart(new Date()));
  const [selectedDay, setSelectedDay] = useState(null);
  const [loadingAppts, setLoadingAppts] = useState(false);
  const [feed, setFeed] = useState(null);
  const [copied, setCopied] = useState(false);

  const activeAdviser = isAdmin ? selected : me;

  const loadStaff = useCallback(async () => {
    const a = await axios.get(`${API}/admin/advisers`, authed).then((r) => r.data);
    setAdvisers(a.advisers || []);
    setMe(a.me);
    setSelected((prev) => prev || a.me || (a.advisers[0]?.key ?? ""));
  }, []);

  const range = useMemo(() => {
    if (view === "week") { const s = weekAnchor; return { start: iso(s), end: iso(addDays(s, 6)) }; }
    return { start: iso(startOfMonth(cursor)), end: iso(endOfMonth(cursor)) };
  }, [view, cursor, weekAnchor]);

  const loadData = useCallback(async () => {
    const u = await axios.get(`${API}/admin/unavailability`, authed).then((r) => r.data);
    setItems(u.items || []);
    if (!activeAdviser) { setAppts([]); return; }
    setLoadingAppts(true);
    try {
      const params = { start: range.start, end: range.end };
      if (isAdmin && selected) params.adviser_key = selected;
      const r = await axios.get(`${API}/admin/appointments`, { ...authed, params }).then((x) => x.data);
      setAppts(r.appointments || []);
    } catch { setAppts([]); } finally { setLoadingAppts(false); }
  }, [range, activeAdviser, isAdmin, selected]);

  const loadFeed = useCallback(async () => {
    if (!activeAdviser) { setFeed(null); return; }
    try {
      const params = {};
      if (isAdmin && selected) params.adviser_key = selected;
      const r = await axios.get(`${API}/admin/calendar-feed`, { ...authed, params }).then((x) => x.data);
      setFeed(r.available ? { ...r, url: `${BACKEND_URL}${r.path}` } : null);
    } catch { setFeed(null); }
  }, [activeAdviser, isAdmin, selected]);

  useEffect(() => { loadStaff(); }, [loadStaff]);
  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => { loadFeed(); }, [loadFeed]);

  const daysOff = useMemo(() => {
    const set = new Set();
    items.filter((i) => !activeAdviser || i.adviser_key === activeAdviser).forEach((i) => set.add(i.date));
    return set;
  }, [items, activeAdviser]);

  const apptsByDay = useMemo(() => {
    const m = {};
    appts.forEach((a) => { (m[a.date] = m[a.date] || []).push(a); });
    return m;
  }, [appts]);

  const grid = useMemo(() => {
    const first = startOfMonth(cursor);
    const gridStart = addDays(first, -((first.getDay() + 6) % 7));
    return Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));
  }, [cursor]);

  const weekDaysArr = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekAnchor, i)), [weekAnchor]);
  const todayIso = iso(new Date());

  const toggleDay = async (dateStr, isOff) => {
    if (!activeAdviser) { toast.error("No adviser profile linked to your account."); return; }
    try {
      if (isOff) {
        await axios.delete(`${API}/admin/unavailability`, { ...authed, params: { date: dateStr, adviser_key: activeAdviser } });
        toast.success("Day marked available.");
      } else {
        const body = { dates: [dateStr] };
        if (isAdmin && selected) body.adviser_key = selected;
        await axios.post(`${API}/admin/unavailability`, body, authed);
        toast.success("Day marked unavailable.");
      }
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update the day.");
    }
  };

  const goPrev = () => view === "week" ? setWeekAnchor(addDays(weekAnchor, -7)) : setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1));
  const goNext = () => view === "week" ? setWeekAnchor(addDays(weekAnchor, 7)) : setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1));
  const goToday = () => { setCursor(startOfMonth(new Date())); setWeekAnchor(weekStart(new Date())); };

  const copyFeed = async () => {
    if (!feed?.url) return;
    try { await navigator.clipboard.writeText(feed.url); }
    catch (err) { console.error("[availability] copy feed link failed", err); toast.error("Could not copy the link."); return; }
    setCopied(true); toast.success("Link copied."); setTimeout(() => setCopied(false), 2000);
  };

  const label = view === "week"
    ? `${weekAnchor.toLocaleDateString("en-GB", { day: "numeric", month: "short" })} – ${addDays(weekAnchor, 6).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}`
    : cursor.toLocaleDateString("en-GB", { month: "long", year: "numeric" });
  const dayAppts = selectedDay ? (apptsByDay[selectedDay] || []) : [];

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-white">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <p className="font-serif text-xl text-brand-black flex items-center gap-2">
            <CalendarOff className="h-5 w-5 text-brand-gold" /> Availability &amp; appointments
          </p>
          <Link to="/admin/enquiries" className="text-sm text-brand-darkgrey hover:text-brand-gold inline-flex items-center gap-1" data-testid="nav-back-enquiries">
            <ArrowLeft className="h-4 w-4" /> Back to enquiries
          </Link>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8 space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <p className="text-sm text-brand-darkgrey max-w-xl">
            Click any weekday to mark yourself unavailable (holiday or sick). Days with appointments show a count —
            click a day to see who is booked in.
          </p>
          {isAdmin && (
            <div>
              <label className="text-xs font-semibold text-brand-black block mb-1">Adviser</label>
              <select value={selected} onChange={(e) => { setSelected(e.target.value); setSelectedDay(null); }} data-testid="availability-adviser"
                className="border border-border bg-white text-sm px-3 py-2 rounded-sm">
                {advisers.map((a) => <option key={a.key} value={a.key}>{a.name}</option>)}
              </select>
            </div>
          )}
        </div>

        {!activeAdviser ? (
          <p className="text-sm text-brand-danger" data-testid="availability-no-profile">
            Your account is not linked to an adviser profile, so you cannot set availability.
          </p>
        ) : (
          <>
            <div className={view === "month" ? "grid lg:grid-cols-3 gap-6" : ""}>
              {/* Calendar */}
              <div className={`border border-border bg-white rounded-sm p-4 ${view === "month" ? "lg:col-span-2" : ""}`} data-testid="availability-calendar">
                <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
                  <div className="flex items-center gap-1 border border-border rounded-sm p-0.5">
                    <button onClick={() => setView("month")} data-testid="view-month"
                      className={`text-xs px-2.5 py-1 rounded-sm inline-flex items-center gap-1 ${view === "month" ? "bg-brand-black text-white" : "text-brand-darkgrey hover:text-brand-gold"}`}>
                      <Grid3x3 className="h-3.5 w-3.5" /> Month
                    </button>
                    <button onClick={() => setView("week")} data-testid="view-week"
                      className={`text-xs px-2.5 py-1 rounded-sm inline-flex items-center gap-1 ${view === "week" ? "bg-brand-black text-white" : "text-brand-darkgrey hover:text-brand-gold"}`}>
                      <CalendarDays className="h-3.5 w-3.5" /> Week
                    </button>
                  </div>
                  <div className="flex items-center gap-3">
                    <button onClick={goPrev} data-testid="cal-prev" className="p-1.5 text-brand-darkgrey hover:text-brand-gold"><ChevronLeft className="h-5 w-5" /></button>
                    <p className="font-serif text-base text-brand-black min-w-[9rem] text-center" data-testid="cal-label">{label}</p>
                    <button onClick={goNext} data-testid="cal-next" className="p-1.5 text-brand-darkgrey hover:text-brand-gold"><ChevronRight className="h-5 w-5" /></button>
                  </div>
                  <button onClick={goToday} data-testid="cal-today" className="text-xs px-2 py-1 border border-border rounded-sm text-brand-darkgrey hover:text-brand-gold">Today</button>
                </div>

                {view === "month" ? (
                  <>
                    <div className="grid grid-cols-7 gap-1 mb-1">
                      {WEEKDAYS.map((w) => <div key={w} className="text-center text-xs font-semibold text-brand-darkgrey py-1">{w}</div>)}
                    </div>
                    <div className="grid grid-cols-7 gap-1">
                      {grid.map((d) => {
                        const ds = iso(d);
                        const inMonth = d.getMonth() === cursor.getMonth();
                        const weekend = d.getDay() === 0 || d.getDay() === 6;
                        const past = ds < todayIso;
                        const off = daysOff.has(ds);
                        const count = (apptsByDay[ds] || []).length;
                        const disabled = weekend || past;
                        const isSel = selectedDay === ds;
                        return (
                          <button key={ds} data-testid={`day-${ds}`}
                            onClick={() => { if (!inMonth) return; setSelectedDay(ds); if (!disabled) toggleDay(ds, off); }}
                            disabled={!inMonth}
                            className={[
                              "relative h-16 rounded-sm border text-left p-1.5 transition-colors",
                              !inMonth ? "opacity-30 cursor-default border-transparent" : "cursor-pointer",
                              off ? "bg-brand-danger/10 border-brand-danger/50" : disabled ? "bg-muted/40 border-border" : "bg-white border-border hover:border-brand-gold",
                              isSel ? "ring-2 ring-brand-gold" : "",
                            ].join(" ")}>
                            <span className={["text-xs font-medium", off ? "text-brand-danger" : past ? "text-muted-foreground" : "text-brand-black"].join(" ")}>{d.getDate()}</span>
                            {off && <span className="block text-[10px] text-brand-danger font-semibold">Off</span>}
                            {count > 0 && (
                              <span className="absolute bottom-1 right-1 inline-flex items-center text-[10px] font-semibold text-brand-black bg-brand-gold/25 rounded-full px-1.5 py-0.5" data-testid={`day-count-${ds}`}>{count}</span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 gap-2" data-testid="week-grid">
                    {weekDaysArr.map((d) => {
                      const ds = iso(d);
                      const weekend = d.getDay() === 0 || d.getDay() === 6;
                      const past = ds < todayIso;
                      const off = daysOff.has(ds);
                      const list = apptsByDay[ds] || [];
                      const disabled = weekend || past;
                      return (
                        <div key={ds} className={`border rounded-sm flex flex-col ${off ? "border-brand-danger/50 bg-brand-danger/5" : "border-border bg-white"}`} data-testid={`week-day-${ds}`}>
                          <button onClick={() => !disabled && toggleDay(ds, off)} disabled={disabled}
                            className={`px-2 py-1.5 border-b text-left ${disabled ? "cursor-default" : "cursor-pointer hover:bg-brand-gold/5"} ${off ? "border-brand-danger/40" : "border-border"}`}
                            data-testid={`week-toggle-${ds}`}>
                            <span className="text-[11px] font-semibold text-brand-darkgrey">{d.toLocaleDateString("en-GB", { weekday: "short" })}</span>
                            <span className={`ml-1 text-sm font-medium ${off ? "text-brand-danger" : "text-brand-black"}`}>{d.getDate()}</span>
                            {off && <span className="block text-[10px] text-brand-danger font-semibold">Unavailable</span>}
                          </button>
                          <div className="p-1.5 space-y-1 min-h-[3rem]">
                            {list.length === 0 ? (
                              <p className="text-[11px] text-muted-foreground px-1 py-1">{weekend ? "—" : "No appts"}</p>
                            ) : list.map((a) => (
                              <div key={a.id} className="rounded-sm bg-brand-gold/10 px-1.5 py-1" data-testid={`week-appt-${a.id}`}>
                                <p className="text-[11px] font-semibold text-brand-black leading-tight">{fmtTime(a.start_time)}</p>
                                <p className="text-[11px] text-brand-black leading-tight truncate">{a.client}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                <div className="flex flex-wrap items-center gap-4 mt-4 text-xs text-brand-darkgrey">
                  <span className="inline-flex items-center gap-1"><span className="h-3 w-3 rounded-sm bg-brand-danger/20 border border-brand-danger/50" /> Unavailable</span>
                  <span className="inline-flex items-center gap-1"><span className="h-3 w-3 rounded-full bg-brand-gold/40" /> Has appointments</span>
                  <span className="inline-flex items-center gap-1"><span className="h-3 w-3 rounded-sm bg-muted/60 border border-border" /> Weekend / past</span>
                </div>
              </div>

              {/* Day detail — month view only */}
              {view === "month" && (
                <div className="border border-border bg-white rounded-sm p-4" data-testid="day-detail">
                  <p className="font-serif text-base text-brand-black flex items-center gap-2 mb-3">
                    <CalendarClock className="h-4 w-4 text-brand-gold" />
                    {selectedDay ? new Date(selectedDay + "T00:00:00").toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" }) : "Select a day"}
                  </p>
                  {!selectedDay ? (
                    <p className="text-sm text-muted-foreground">Click a day in the calendar to see appointments and toggle availability.</p>
                  ) : (
                    <>
                      {daysOff.has(selectedDay) && <p className="text-xs text-brand-danger font-semibold mb-3" data-testid="day-off-note">Marked unavailable — clients cannot book this day.</p>}
                      {loadingAppts ? <p className="text-sm text-muted-foreground">Loading…</p> : dayAppts.length === 0 ? (
                        <p className="text-sm text-muted-foreground" data-testid="day-no-appts">No appointments booked.</p>
                      ) : (
                        <ul className="space-y-2" data-testid="day-appts">
                          {dayAppts.map((a) => (
                            <li key={a.id} className="border border-border rounded-sm px-3 py-2" data-testid={`appt-${a.id}`}>
                              <div className="flex items-center justify-between">
                                <span className="text-sm font-semibold text-brand-black">{fmtTime(a.start_time)}–{fmtTime(a.end_time)}</span>
                                <span className="text-[10px] uppercase tracking-wide text-brand-darkgrey">{a.label}</span>
                              </div>
                              <p className="text-sm text-brand-black">{a.client}</p>
                            </li>
                          ))}
                        </ul>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Diary sync — read-only, one-way */}
            {feed && (
              <div className="border border-border bg-white rounded-sm p-5" data-testid="calendar-sync">
                <p className="font-serif text-base text-brand-black flex items-center gap-2">
                  <ArrowRightCircle className="h-4 w-4 text-brand-gold" /> Sync to your own calendar
                </p>
                <div className="mt-3 flex items-start gap-2 rounded-sm bg-brand-gold/10 border border-brand-gold/30 px-3 py-2.5" data-testid="sync-readonly-note">
                  <ShieldCheck className="h-4 w-4 text-brand-gold mt-0.5 shrink-0" />
                  <p className="text-sm text-brand-black">
                    <span className="font-semibold">One‑way and read‑only.</span> Your Family Court Guide appointments will
                    appear in your personal Google or Outlook calendar and refresh automatically. Nothing from your personal
                    calendar is ever brought into this app, and no one can add, edit or cancel appointments through this link.
                    To cancel or reschedule, always use GoHighLevel. Keep this link private to you.
                  </p>
                </div>

                <label className="text-xs font-semibold text-brand-black block mt-4 mb-1">Your private calendar link</label>
                <div className="flex gap-2">
                  <input readOnly value={feed.url} data-testid="sync-url" onFocus={(e) => e.target.select()}
                    className="flex-1 border border-border bg-muted/40 text-xs px-3 py-2 rounded-sm text-brand-darkgrey font-mono" />
                  <Button onClick={copyFeed} data-testid="sync-copy" className="bg-brand-black text-white hover:bg-brand-gold rounded-sm shrink-0">
                    {copied ? <Check className="h-4 w-4 mr-1" /> : <Copy className="h-4 w-4 mr-1" />} {copied ? "Copied" : "Copy"}
                  </Button>
                </div>

                <div className="mt-3 text-xs" data-testid="sync-health">
                  {feed.last_fetched_at ? (
                    <span className="inline-flex items-center gap-1.5 text-brand-darkgrey">
                      <RefreshCw className="h-3.5 w-3.5 text-brand-gold" />
                      Your calendar app last synced this feed{" "}
                      <span className="font-semibold text-brand-black">{relTime(feed.last_fetched_at)}</span>
                      {typeof feed.event_count === "number" && <>{` · ${feed.event_count} appointment${feed.event_count === 1 ? "" : "s"} in the feed`}</>}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 text-muted-foreground">
                      <RefreshCw className="h-3.5 w-3.5" />
                      Not synced yet — once you add the link to your calendar, the time of its last refresh will show here.
                    </span>
                  )}
                </div>

                <div className="grid sm:grid-cols-2 gap-5 mt-5">
                  <div data-testid="sync-google">
                    <p className="text-sm font-semibold text-brand-black mb-1">Add to Google Calendar</p>
                    <ol className="text-sm text-brand-darkgrey list-decimal ml-4 space-y-1">
                      <li>Open Google Calendar on a computer.</li>
                      <li>Next to <span className="font-medium">“Other calendars”</span> click <span className="font-medium">+</span> → <span className="font-medium">From URL</span>.</li>
                      <li>Paste the link above and click <span className="font-medium">Add calendar</span>.</li>
                      <li>Your appointments appear within a few minutes and update on their own.</li>
                    </ol>
                  </div>
                  <div data-testid="sync-outlook">
                    <p className="text-sm font-semibold text-brand-black mb-1">Add to Outlook</p>
                    <ol className="text-sm text-brand-darkgrey list-decimal ml-4 space-y-1">
                      <li>Open Outlook and go to <span className="font-medium">Add calendar</span>.</li>
                      <li>Choose <span className="font-medium">Subscribe from web</span>.</li>
                      <li>Paste the link above, give it a name, and click <span className="font-medium">Import / Subscribe</span>.</li>
                      <li>Outlook keeps it up to date automatically.</li>
                    </ol>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-4">
                  Note: subscribed calendars are read‑only by design — this is what keeps the sync one‑way. Refresh timing is
                  controlled by Google/Outlook (usually every few hours).
                </p>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
